import React from "react";
import { createStyles, Grid, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Autocomplete } from "@material-ui/lab";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

import CardCommon from "../../../../components/card/CardCommon";
import TextfieldCommon from "../../../../components/textField/TextfieldCommon";
import { CustomTheme } from "../../../../types/customTheme";
import { DATE_PRESETS, HistoricalFilterValues } from "./historicalUtils";

/**
 * INSIGHT-HIST-12 — the shared controls for every historical dashboard.
 *
 * Tickets 13, 14 and 15 reuse this, so the dates and filters behave the same
 * on every screen. The styling follows the other Insight pages: CardCommon for
 * the surface, TextfieldCommon for inputs and the same Autocomplete treatment
 * as the reset-hour selector.
 */

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    autoComplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_highlight_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: "none",
        color: theme.palette.custom.orange.contrastText,
      },
    },
    label: {
      color: theme.palette.custom.orange.contrastText,
      marginBottom: "2px",
    },
  }),
);

export interface HistoricalFiltersProps {
  values: HistoricalFilterValues;
  onChange: (values: HistoricalFilterValues) => void;
  /** Station is only worth showing on the station screen. */
  showStation?: boolean;
  /** Driver is only worth showing on the delivery screen. */
  showDriver?: boolean;
  disabled?: boolean;
}

const HistoricalFilters: React.FunctionComponent<HistoricalFiltersProps> = ({
  values,
  onChange,
  showStation = false,
  showDriver = false,
  disabled = false,
}) => {
  const classes = useStyles();

  const set = (key: keyof HistoricalFilterValues) => (event: any) => {
    onChange({ ...values, [key]: event.target.value });
  };

  const selectedPreset =
    DATE_PRESETS.find((preset) => preset.value === (values.preset || "today")) ||
    DATE_PRESETS[0];

  const isCustom = selectedPreset.value === "custom";

  const field = (
    key: keyof HistoricalFilterValues,
    label: string,
    placeholder?: string,
    type?: string,
  ) => (
    <Grid item xs={6} sm={4} md={2}>
      <Typography variant="caption" className={classes.label}>
        {label}
      </Typography>
      <TextfieldCommon
        id={`historical-filter-${key}`}
        name={key}
        type={type || "text"}
        value={values[key] || ""}
        onChange={set(key)}
        disabled={disabled}
        margin="dense"
        inputProps={{ placeholder }}
      />
    </Grid>
  );

  return (
    <CardCommon
      backgroundColor="entity_highlight_background"
      style={{ padding: "20px" }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={2}>
          <Typography variant="caption" className={classes.label}>
            Dates
          </Typography>
          <Autocomplete
            options={DATE_PRESETS}
            value={selectedPreset}
            onChange={(event: any, option: any) =>
              onChange({ ...values, preset: option?.value || "today" })
            }
            getOptionLabel={(option: any) => option.label}
            disableClearable
            disabled={disabled}
            size="small"
            popupIcon={<ArrowDropDownIcon />}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select dates" variant="outlined" />
            )}
            classes={{ root: classes.autoComplete }}
          />
        </Grid>

        {isCustom && (
          <>
            {field("startDate", "From", undefined, "date")}
            {field("endDate", "To", undefined, "date")}
          </>
        )}

        {field("fromHour", "From hour", "0-23", "number")}
        {field("toHour", "To hour", "0-23", "number")}
        {field("mode", "Mode", "delivery")}
        {field("platform", "Platform", "web")}
        {field("channel", "Channel", "online")}
        {showStation && field("stationId", "Station", "KDS1")}
        {showDriver && field("driverId", "Driver")}
      </Grid>
    </CardCommon>
  );
};

export default HistoricalFilters;
